import React, { useEffect, useRef, useState, useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { useFormik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Container,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  ButtonGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormHelperText,
  Hidden,
} from '@material-ui/core';
import PeopleIcon from '@material-ui/icons/People';
import PhotoIcon from '@material-ui/icons/Photo';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import NoteIcon from '@material-ui/icons/Note';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import DeleteIcon from '@material-ui/icons/Delete';
import FolderIcon from '@material-ui/icons/Folder';
import Pagination from '@material-ui/lab/Pagination';
import { VariantContext } from '../../hooks/VariantContext';
import {
  canAddPreviewToCart,
  createPreviewIdentity,
  createPreviewState,
  previewIdentitiesMatch,
  shouldAcceptPreviewResponse,
} from '../print-preview-state';
import { createCheckoutAttemptId } from '../checkout-attempt';
import { clearPrintCheckoutAttempt, loadPrintCheckoutAttempt, savePrintCheckoutAttempt } from '../print-checkout-attempt';
import { resolvePrintCartCheckoutMetadata } from '../print-cart-checkout';
import { getResumablePrintPayments } from '../print-payment-resume';
import PrintPaymentResumeList from '../PrintPaymentResumeList';
import { getPrintCartLineRenewalValues, replacePrintCartLinePreview } from '../print-cart-renewal';
import {
  clearPrintCart,
  hasCurrentPreview,
  loadPrintCart,
  savePrintCart,
  shouldOpenRestoredPrintCart,
} from '../print-cart-storage';
import { UserContext } from '../../hooks/UserContext';
import StripeCheckoutModal from '../StripeCheckoutModal';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: '50px',
    marginBottom: '50px',
  },
  printList: {
    position: 'relative',
    top: 0,
    backgroundColor: theme.palette.background.paper,
  },
  inline: {
    display: 'inline',
  },
  avatarWrapper: {
    padding: '15px',
  },
  avatar: {
    width: '240px',
    height: '160px',
  },
  detailWrapper: {
    padding: '15px',
  },
  metrics: {
    position: 'absolute',
    top: '15px',
    right: '15px',
  },
  location: {
    position: 'absolute',
    bottom: '20px',
  },
  badge: {
    right: -3,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
  bookOutputCard: {
    minHeight: '126px',
  },
  bookFormatCard: {
    minHeight: '88px',
    [theme.breakpoints.down('sm')]: {
      minHeight: '112px',
    },
  },
  bookTypeCard: {
    minHeight: '192px',
    [theme.breakpoints.down('sm')]: {
      minHeight: '214px',
    },
  },
  cartItem: {
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
  },
  cartQuantity: {
    flex: '0 0 auto',
    margin: 0,
  },
  cartProduct: {
    flex: '1 1 180px',
    minWidth: 0,
    margin: 0,
    overflowWrap: 'anywhere',
  },
  cartPrice: {
    flex: '0 1 150px',
    minWidth: 0,
    margin: 0,
  },
}));

const Print = (props) => {
  const classes = useStyles();

  const { appVariant } = useContext(VariantContext);
  const { user } = useContext(UserContext);

  const history = useHistory();

  const [loading, setLoading] = useState(null);
  const [ready, setReady] = useState(null);
  const [prints, setPrints] = useState(null);
  const [currentPrint, setCurrentPrint] = useState(null);
  const [brandboxPreview, setBrandboxPreview] = useState(false);
  const [previewState, setPreviewState] = useState(null);
  const previewRequestIdRef = useRef(0);
  const currentPreviewIdentityRef = useRef(null);
  const [waitingForPdfDownload, setWaitingForPdfDownload] = useState(null);
  const [showPdfDownload, setShowPdfDownload] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadFilename, setDownloadFilename] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [alreadyPrinted, setAlreadyPrinted] = useState(null);
  const [brandboxError, setBrandboxError] = useState(null);
  const [printsInCart, setPrintsInCart] = useState(loadPrintCart);
  const restoredCheckoutAttempt = useRef(loadPrintCheckoutAttempt(printsInCart)).current;
  const [cartOpen, setCartOpen] = useState(() => shouldOpenRestoredPrintCart(printsInCart));
  const [quantity, setQuantity] = useState(1);
  const [paymentStart, setPaymentStart] = useState(false);
  const [paymentIsComplete, setPaymentIsComplete] = useState(null);
  const [fulfillmentError, setFulfillmentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [checkoutAttemptId, setCheckoutAttemptId] = useState(restoredCheckoutAttempt?.checkoutAttemptId || null);
  const [cartPersistenceError, setCartPersistenceError] = useState(false);
  const [renewalLineItemId, setRenewalLineItemId] = useState(null);
  const [resumablePayments, setResumablePayments] = useState([]);
  const [resumePayment, setResumePayment] = useState(null);
  const [resumePaymentIntent, setResumePaymentIntent] = useState(null);

  const numPagesToPrint = previewState?.totalPages || null;

  const [coupon, setCoupon] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);

  const preselectPrintId = props.location.search.split('?id=')[1];

  const url = new URL(window.location);
  const payloadParam = url.searchParams.get('payload');
  const intentParam = url.searchParams.get('payment_intent');
  const statusParam = url.searchParams.get('redirect_status');
  const userId = user?.id;

  useEffect(() => {
    if (!userId) {
      setResumePayment(null);
      setResumePaymentIntent(null);
      setPaymentStart(false);
      return;
    }
    fetch(`${process.env.REACT_APP_API_URL}/payments/get-own-payments?sortBy=createdAt:desc&limit=50`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((response) => (response.ok ? response.json() : { results: [] }))
      .then((data) => {
        const ownPendingPayments = getResumablePrintPayments(data.results);
        setResumablePayments(ownPendingPayments);
      })
      .catch(() => setResumablePayments([]));
  }, [userId]);

  useEffect(() => {
    // Send print order after redirection from SofortÜberweisung payment page
    if (payloadParam && intentParam) {
      if (statusParam === 'failed') return setPaymentIsComplete(false);

      let payload;
      try {
        payload = JSON.parse(payloadParam);
      } catch (error) {
        //console.error('ERROR SOFORT REDIRECT URL:', error);
        setPaymentIsComplete(false);
        setPaymentSuccess(false);
        return false;
      }

      payload.paymentId = intentParam;
      console.log('POST PURCHASE AFTER REDIRECT', payload);
      const purchase = fetch(`${process.env.REACT_APP_API_URL}/payments/purchase-product`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.productId || data.paymentStatus === 'paid') {
            setPaymentSuccess(true);
            generatePaidPrint(data);
          }

          return data;
        })
        .then((result) => {
          if (result.error) {
            // The card had an error when trying to attach it to a customer.
            throw result;
          }
          return result;
        });
    }

    // TODO: integrate into print order workflow later
    fetch(`${process.env.REACT_APP_API_URL}/prints`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setPrints(data.results);
        if (data.results[0]) {
          // TODO: set current print
          //setCurrentPrint(preselectPrintId || data.results[0].id);
        }
      })
      .catch((error) => {
        console.error(error);
      });
    fetch(`${process.env.REACT_APP_API_URL}/products`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('PRODUCTS RESPONSE:', data);

        setShopProducts(Array.isArray(data) ? data : data.results || []);
      })
      .catch((error) => {
        console.error('PRODUCTS ERROR:', error);
      });
  }, []);

  const validationSchema = Yup.object({
    printId: Yup.string().required('Bitte Print auswählen'),
    format: Yup.string().required('Bitte Print-Format auswählen'),
    type: Yup.string().required('Bitte Print-Typ auswählen'),
    coupon_code: Yup.string().test('checkCouponCode', 'Dieser Coupon Code ist ungültig', async (value) => {
      if (!value) return true;
      return fetch(`${process.env.REACT_APP_API_URL}/products/coupon-code/${value}`).then(async (res) => {
        const { coupon } = await res.json();
        const discountAttribute = coupon.attributes.find((attribute) => attribute.name === 'Geldrabatt');
        const valid = !!discountAttribute;
        if (valid) setCoupon(coupon);

        return valid;
      });
    }),
  });

  const resetPreview = () => {
    previewRequestIdRef.current += 1;
    setPreviewState(null);
    setLoading(false);
    setReady(false);
    setBrandboxPreview(null);
  };

  const { handleSubmit, handleChange, setFieldValue, setValues, values, touched, errors } = useFormik({
    initialValues: {
      printId: prints?.length < 2 && prints[0] ? prints[0].id : '',
      output: 'hardcover',
      format: '211x211',
      type: 'day',
      coupon_code: '',
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    onSubmit: (values, { setStatus, setErrors, setFieldError }) => {
      if (ready) {
        console.log('FORMIK SUBMIT: ORDER PRINT', values);
        //const redirectAfterLogin = 'subscription';
        //history.push(redirectAfterLogin);
        //return;
      }

      setLoading(true);

      const requestIdentity = createPreviewIdentity(values);
      const requestId = previewRequestIdRef.current + 1;
      previewRequestIdRef.current = requestId;
      currentPreviewIdentityRef.current = requestIdentity;

      const payload = {
        ...values,
        preview: true,
      };

      fetch(
        `${process.env.REACT_APP_API_URL}/templates/generate`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          responseType: 'json', // 'blob'
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
        { responseType: 'arraybuffer' }
      )
        /*.then((response) => {
          if (response.status !== 201) return;
          response.blob().then((blob) => {
            var url = URL.createObjectURL(blob);
            //saveAs(url, "preview.pdf");
            //window.open(url);
            //console.log(`https://drive.google.com/viewerng/viewer?embedded=true&url=${url}`);
            setLoading(false);
            setBrandboxPreview({ url: blob });
            setReady(true);
            return blob;
          });
        })*/
        .then(async (response) => {
          if (response.status !== 201) throw new Error('Preview generation failed');
          const json = await response.json();
          if (
            !shouldAcceptPreviewResponse({
              requestId,
              activeRequestId: previewRequestIdRef.current,
              requestIdentity,
              currentIdentity: currentPreviewIdentityRef.current,
            })
          ) {
            return;
          }

          const pages =
            Number(json?.numberOfPages) || Number(json?.pages) || Number(json?.numPages) || Number(json?.pageCount) || 24;
          setPreviewState(
            createPreviewState({ token: json?.previewToken || null, identity: requestIdentity, totalPages: pages })
          );
          setLoading(false);
          setBrandboxPreview(true);
          setReady(true);
        })
        .catch((error) => {
          if (requestId === previewRequestIdRef.current) setLoading(false);
          console.error('GENERATE PREVIEW ERROR:', error);
        });
    },
  });

  useEffect(() => {
    currentPreviewIdentityRef.current = createPreviewIdentity({
      printId: values.printId,
      format: values.format,
      type: values.type,
    });
  }, [values.printId, values.format, values.type]);

  const handlePreviewIdentityChange = (event) => {
    const nextIdentity = createPreviewIdentity({ ...values, [event.target.name]: event.target.value });
    if (!previewIdentitiesMatch(currentPreviewIdentityRef.current, nextIdentity)) {
      resetPreview();
      currentPreviewIdentityRef.current = nextIdentity;
    }
    handleChange(event);
  };

  const getProductIdOfSelection = () => {
    const productIds = {
      hardcover_211x211: 847,
      softcover_211x211: 478,
      hardcover_210x297: 886,
      softcover_210x297: 888,
      pdf_211x211: 848,
      pdf_210x297: 848,
    };

    return productIds[`${values.output}_${values.format}`];
  };

  const additionalPageProduct = shopProducts.find((item) => Number(item.id) === 1384);

  const additionalPagePrice = additionalPageProduct ? Number(additionalPageProduct.price) : 0.35;

  const getProductOfSelection = () => {
    const productId = getProductIdOfSelection();

    return shopProducts.find((item) => Number(item.id) === Number(productId));
  };

  const getBasePriceOfSelection = (numPages = 24) => {
    const product = getProductOfSelection();

    if (!product) return 0;

    if (values.output === 'pdf') {
      return Number(product.price);
    }

    const bookCount = Math.ceil(Number(numPages || 24) / 200);

    return Number(product.price) * bookCount;
  };

  const getExtraPagesCount = (numPages = 24) => {
    if (values.output === 'pdf') return 0;

    const pages = Number(numPages || 24);

    const bookCount = Math.ceil(pages / 200);

    const includedPages = bookCount * 24;

    return Math.max(0, pages - includedPages);
  };

  const getExtraPagesPrice = (numPages = 24) => {
    return getExtraPagesCount(numPages) * additionalPagePrice;
  };

  const getPriceOfSelection = (numPages = 24) => {
    return getBasePriceOfSelection(numPages) + getExtraPagesPrice(numPages);
  };

  const getCouponDiscount = () => {
    if (!coupon) return 0;

    const discountAttribute = coupon.attributes.find((attribute) => attribute.name === 'Geldrabatt');

    const discount = discountAttribute ? Number(discountAttribute.options[0]) : 0;

    return discount;
  };

  const countryCode = user?.addresses?.shipping ? user?.addresses?.shipping.country : user?.addresses?.billing.country;
  const shippingCosts = {
    de: 7,
    at: 7,
    ch: 17.5,
  };
  const getShippingCosts = () => {
    if (values.output === 'pdf') return 0;
    return shippingCosts[countryCode];
  };

  const persistPrintCart = (lines) => {
    const result = savePrintCart(lines);
    if (!result.ok) {
      setCartPersistenceError(true);
      return false;
    }
    setPrintsInCart(lines);
    setCartPersistenceError(false);
    clearPrintCheckoutAttempt();
    setCheckoutAttemptId(null);
    return true;
  };

  const addPrintToCart = (values) => {
    const currentIdentity = createPreviewIdentity(values);
    if (!canAddPreviewToCart(previewState, currentIdentity)) {
      resetPreview();
      return;
    }
    const clientLineItemId = window.crypto?.randomUUID?.() || `line-${Date.now()}-${Math.random()}`;
    const line = {
      clientLineItemId,
      printId: values.printId,
      fulfillmentKind: values.output === 'pdf' ? 'digital' : 'physical',
      outputFormat: values.output === 'pdf' ? 'pdf' : 'print',
      binding: values.output === 'pdf' ? null : values.output,
      format: values.format,
      productType: values.type,
      contentFinish: 'Standard',
      quantity: 1,
      totalPages: previewState.totalPages,
      previewToken: previewState.token,
      printTitle: prints.find((print) => print.id === values.printId)?.title || '',
      displayPrice: getPriceOfSelection(Number(numPagesToPrint || 24)),
    };
    if (persistPrintCart([...printsInCart, line])) setCartOpen(true);
  };

  const startPrintPreviewRenewal = (line) => {
    const renewalValues = getPrintCartLineRenewalValues(line);
    setRenewalLineItemId(line.clientLineItemId);
    resetPreview();
    currentPreviewIdentityRef.current = createPreviewIdentity(renewalValues);
    setValues(renewalValues);
    setCartOpen(false);
  };

  const cancelPrintPreviewRenewal = () => {
    resetPreview();
    setRenewalLineItemId(null);
    setCartPersistenceError(false);
    setCartOpen(true);
  };

  const renewPrintCartLinePreview = (values) => {
    const currentIdentity = createPreviewIdentity(values);
    if (!canAddPreviewToCart(previewState, currentIdentity)) {
      resetPreview();
      return;
    }

    const targetLine = printsInCart.find((line) => line.clientLineItemId === renewalLineItemId);
    if (!targetLine) {
      setCartPersistenceError(true);
      return;
    }

    const result = replacePrintCartLinePreview(printsInCart, renewalLineItemId, {
      previewToken: previewState.token,
      totalPages: previewState.totalPages,
      displayPrice: getPriceOfSelection(previewState.totalPages),
      printTitle: prints.find((print) => print.id === targetLine.printId)?.title || targetLine.printTitle,
    });
    if (!result.ok || !persistPrintCart(result.cart)) return;

    setRenewalLineItemId(null);
    setCartOpen(true);
  };

  const handleIncrement = (line) => {
    persistPrintCart(
      printsInCart.map((item) =>
        item.clientLineItemId === line.clientLineItemId ? { ...item, quantity: Math.min(100, item.quantity + 1) } : item
      )
    );
  };

  const handleDecrement = (line) => {
    persistPrintCart(
      printsInCart.map((item) =>
        item.clientLineItemId === line.clientLineItemId ? { ...item, quantity: Math.max(1, item.quantity - 1) } : item
      )
    );
  };

  const handleRemove = (line) => {
    const remainingLines = printsInCart.filter((item) => item.clientLineItemId !== line.clientLineItemId);
    if (persistPrintCart(remainingLines) && remainingLines.length === 0) setCartOpen(false);
  };

  const generatePaidPrint = async (payment) => {
    setPaymentIsComplete(true);
    setFulfillmentError(null);
    setWaitingForPdfDownload(payment.lines.some((line) => line.fulfillmentKind === 'digital'));
    try {
      for (const line of payment.lines) {
        const generated = await fetch(process.env.REACT_APP_API_URL + '/templates/generate', {
          method: 'POST',
          body: JSON.stringify({ paymentId: payment.paymentId, lineItemId: line.lineItemId, preview: false }),
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        if (!generated.ok) throw new Error('Product generation failed');
      }
      if (payment.lines.some((line) => line.fulfillmentKind === 'physical')) {
        const fulfilled = await fetch(
          process.env.REACT_APP_API_URL + '/payments/' + payment.paymentId + '/physical-fulfillment',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') },
          }
        );
        if (!fulfilled.ok) throw new Error('Grouped print submission failed');
      }
      const artifactResponse = await fetch(process.env.REACT_APP_API_URL + '/payments/' + payment.paymentId + '/artifacts', {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      });
      const artifactData = await artifactResponse.json();
      setDownloads(artifactData.artifacts || []);
      setWaitingForPdfDownload(false);
      setShowPdfDownload((artifactData.artifacts || []).length > 0);
      if (clearPrintCart().ok) {
        setPrintsInCart([]);
        setCartPersistenceError(false);
      } else {
        setCartPersistenceError(true);
      }
      clearPrintCheckoutAttempt();
      setCheckoutAttemptId(null);
    } catch (error) {
      console.error('ORDER FULFILLMENT ERROR:', error);
      setFulfillmentError(true);
    }
  };

  const downloadArtifact = async (artifact) => {
    const response = await fetch(process.env.REACT_APP_API_URL + artifact.downloadUrl.replace('/v1', ''), {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
    });
    if (!response.ok) return;
    saveAs(await response.blob(), artifact.filename);
  };
  if (intentParam && paymentIsComplete === null)
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '80vh' }}
      >
        <CircularProgress style={{ margin: '0 auto' }} />
      </Grid>
    );

  if (paymentIsComplete !== null) {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '80vh' }}
      >
        <Grid item xs={6} style={{ maxWidth: '90vw' }}>
          <Typography variant="h5" gutterBottom style={{ padding: '10px' }}>
            {paymentIsComplete === true && fulfillmentError === true && 'Die Zahlung war erfolgreich.'}
            {paymentIsComplete === true &&
              fulfillmentError !== true &&
              waitingForPdfDownload === null &&
              'Dein Buch wird bald gedruckt.'}
            {paymentIsComplete === true &&
              !alreadyPrinted &&
              waitingForPdfDownload === true &&
              'Deine PDF wird generiert. Bitte warte einen Moment.'}
            {/*paymentIsComplete === true && alreadyPrinted && <span>Deine PDF wurde schon generiert, du kannst sie in einem <a href="/mybloomup">Profil</a> unter Belege herunterladen.</span>*/}
            {paymentIsComplete === true && showPdfDownload === true && waitingForPdfDownload === false && (
              <span>
                {downloads.map((artifact) => (
                  <Button key={artifact.artifactId} onClick={() => downloadArtifact(artifact)}>
                    {artifact.filename} herunterladen
                  </Button>
                ))}
              </span>
            )}
            {paymentIsComplete === false && 'Bei der Zahlung ist ein Fehler aufgetreten.'}
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ maxWidth: '90vw' }}>
          <Typography variant="body1">
            {paymentIsComplete === true &&
              fulfillmentError === true &&
              'Bei der Erstellung deines Druckauftrags ist ein Fehler aufgetreten. Bitte führe keine erneute Zahlung durch und wende dich an den Support.'}
            {paymentIsComplete === true && fulfillmentError !== true && 'Du erhältst in Kürze eine Bestätigung per E-Mail.'}
            {paymentIsComplete === false && 'Bitte versuche es später noch einmal oder wende dich an den Support.'}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (cartOpen) {
    const cartProductLabels = {
      day: 'Lebensbuch',
      photostream: 'Fotostream',
      '211x211': 'quadratisch',
      '210x297': 'A4 Hochformat',
    };
    const getCartShippingCosts = () =>
      printsInCart.some((print) => print.fulfillmentKind === 'physical')
        ? shippingCosts[countryCode] || shippingCosts.de
        : 0;
    const getCartTotal = () => {
      return printsInCart.reduce((sum, print) => sum + print.quantity * print.displayPrice, 0);
    };
    const cartHasStalePreview = printsInCart.some((print) => !hasCurrentPreview(print));
    const checkoutMetadata = resolvePrintCartCheckoutMetadata(prints, printsInCart);

    return (
      <div className={classes.root}>
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom>
            <b>Warenkorb</b>
          </Typography>
          <Grid container spacing={0} style={{ minHeight: '70vh' }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Prints</Typography>
                  {cartPersistenceError && (
                    <Typography color="error">Der Warenkorb konnte nicht dauerhaft gespeichert werden.</Typography>
                  )}
                  {printsInCart.map((print) => (
                    <div key={print.clientLineItemId}>
                      <List dense={true}>
                        <ListItem className={classes.cartItem}>
                          <ListItemText className={classes.cartQuantity}>
                            <ButtonGroup size="small" aria-label="small outlined button group">
                              <Button disabled={print.quantity <= 1} onClick={() => handleDecrement(print)}>
                                -
                              </Button>
                              <Button disabled>{print.quantity}</Button>
                              <Button disabled={print.quantity >= 100} onClick={() => handleIncrement(print)}>
                                +
                              </Button>
                            </ButtonGroup>
                          </ListItemText>
                          <ListItemText
                            className={classes.cartProduct}
                            primary={
                              print.quantity +
                              ' x ' +
                              cartProductLabels[print.productType] +
                              ' (' +
                              (print.fulfillmentKind === 'digital' ? 'PDF' : print.binding) +
                              ', ' +
                              cartProductLabels[print.format] +
                              ')'
                            }
                            secondary={
                              <>
                                <Typography variant="caption">
                                  {print.printTitle} · {print.totalPages} Seiten
                                  {print.totalPages > 200 && print.fulfillmentKind === 'physical'
                                    ? ' · ' + Math.ceil(print.totalPages / 200) + ' technische Teilbücher'
                                    : ''}
                                </Typography>
                                <br />
                                <Typography variant="caption">{print.contentFinish}</Typography>
                              </>
                            }
                          />
                          <ListItemText
                            className={classes.cartPrice}
                            primary={new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                              print.quantity * print.displayPrice
                            )}
                            secondary={
                              process.env.REACT_APP_API_URL === 'https://api-stage.getbloomup.com/v1'
                                ? 'Preis wird beim Checkout serverseitig bestätigt'
                                : null
                            }
                          />
                          <Button startIcon={<DeleteIcon />} onClick={() => handleRemove(print)}>
                            entfernen
                          </Button>
                          {!hasCurrentPreview(print) && (
                            <Button onClick={() => startPrintPreviewRenewal(print)}>Vorschau erneuern</Button>
                          )}
                        </ListItem>
                      </List>
                    </div>
                  ))}
                </CardContent>
                <Box p={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    onClick={() => {
                      history.push('/bloomprint');
                      setCartOpen(false);
                    }}
                  >
                    weiter einkaufen
                  </Button>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={6} md={4}>
              <Grid container justifyContent="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6">{'Dein Warenkorb'}</Typography>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Grid container justifyContent="center">
                    <Grid item xs={6}>
                      <Typography>{`Zwischensumme`}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right">
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(getCartTotal())}
                      </Typography>
                      <Typography align="right">
                        <small>{`Preis inkl. 19% MwSt.`}</small>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                {printsInCart.some((item) => item.fulfillmentKind === 'physical') && (
                  <Grid item xs={12} md={8}>
                    <Grid container justifyContent="center">
                      <Grid item xs={6}>
                        <Typography>{'Versand'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography align="right">
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                            getCartShippingCosts()
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {coupon && (
                  <Grid item xs={12} md={8}>
                    <Grid container justifyContent="center">
                      <Grid item xs={6}>
                        <Typography>{'Gutschein'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography align="right">
                          -
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                            getCouponDiscount()
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12} md={8}>
                  <Grid container justifyContent="center">
                    <Grid item xs={6}>
                      <Typography>
                        <b>{'Summe'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right">
                        <b>
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                            getCartTotal() + getCartShippingCosts() - getCouponDiscount()
                          )}
                        </b>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={8}>
                  <br />
                  <Button
                    onClick={() => {
                      setCheckoutAttemptId((current) => {
                        const attemptId = current || createCheckoutAttemptId();
                        savePrintCheckoutAttempt(printsInCart, attemptId);
                        return attemptId;
                      });
                      setPaymentStart(true);
                    }}
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={cartHasStalePreview || !checkoutMetadata.ready}
                  >
                    Zur Kasse
                  </Button>
                  {cartHasStalePreview && (
                    <Typography color="error" variant="caption">
                      Mindestens eine Warenkorbposition benötigt eine neue passende Vorschau.
                    </Typography>
                  )}
                  {!cartHasStalePreview && !checkoutMetadata.ready && (
                    <Typography color="error" variant="caption">
                      {checkoutMetadata.reason === 'prints-loading'
                        ? 'Produktdaten werden geladen.'
                        : 'Produktdaten für mindestens eine Warenkorbposition fehlen.'}
                    </Typography>
                  )}
                  <br />
                  <br />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>

        {paymentStart && !resumePayment && checkoutMetadata.ready && (
          <StripeCheckoutModal
            modalOpen={paymentStart}
            onComplete={(payment) => generatePaidPrint(payment)}
            onCancel={() => {
              setPaymentStart(false);
              setPaymentIsComplete(null);
            }}
            product="print"
            paymentMethods={['sofort', 'card']}
            params={checkoutMetadata.params}
            cartContents={[
              // preparing for shopping cart system that comes later
              ...printsInCart,
              // { printId: values.printId, quantity: quantity, product: 847 /* <- fetch productIds from woocommerce API */ },
            ]}
            checkoutAttemptId={checkoutAttemptId}
            onIntentCreated={(intent) => savePrintCheckoutAttempt(printsInCart, checkoutAttemptId, intent.paymentDraftId)}
            previewReady={!cartHasStalePreview}
            amount={getCartTotal()}
            shippingCosts={getCartShippingCosts()}
            couponCode={values.coupon_code}
          />
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="lg">
        {resumablePayments.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6">Unvollständige Zahlungen</Typography>
              <PrintPaymentResumeList
                payments={resumablePayments}
                onResumeStart={() => {
                  setPaymentStart(false);
                  setResumePayment(null);
                  setResumePaymentIntent(null);
                }}
                onResumeReady={(payment, intent) => {
                  setResumePayment(payment);
                  setResumePaymentIntent(intent);
                  setPaymentStart(true);
                }}
              />
            </CardContent>
          </Card>
        )}
        <form onSubmit={handleSubmit} className={classes.form} noValidate>
          <Grid container>
            <Grid item md={5} xs={12}>
              <Typography variant="h3" gutterBottom>
                {appVariant === 'bloomup' ? 'Lebensbuch bestellen' : 'Projekt-Print beauftragen'}
              </Typography>
              <br />
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography>
                    <b>{'1. Wähle ein Fotobuch aus'}</b>
                  </Typography>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Card elevation={5} style={{ minWidth: '290px' }}>
                    <CardContent>
                      <FormControl fullWidth variant="outlined" margin="none">
                        <InputLabel id="print-select-label">
                          {!prints?.length ? 'Keine Prints vorhanden' : 'Print'}
                        </InputLabel>
                        <Select
                          id="printId"
                          name="printId"
                          value={values.printId}
                          label="Print"
                          disabled={!prints?.length || Boolean(renewalLineItemId)}
                          onChange={handlePreviewIdentityChange}
                          error={touched.printId && Boolean(errors.printId)}
                        >
                          {prints?.map((print) => (
                            <MenuItem value={print.id} key={print.id}>
                              {print.title}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.printId && errors.printId && (
                          <FormHelperText htmlFor="render-select" error>
                            {errors.printId}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                <Hidden xsDown>
                  <Grid item md={4} xs={1}>
                    &nbsp;
                  </Grid>
                </Hidden>

                <Grid item xs={12}>
                  <Typography gutterBottom>
                    <b>{'2. Wie soll der Print aussehen?'}</b>
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" disabled={Boolean(renewalLineItemId)}>
                    <RadioGroup aria-label="output" name="output" value={values.output} onChange={handleChange}>
                      <Grid container spacing={3}>
                        <Grid item md={5} xs={6}>
                          <Card elevation={5} className={classes.bookOutputCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="hardcover"
                                    checked={values.output === 'hardcover'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="Hard-cover"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <br />
                              <Typography variant="caption">
                                mind. <b>24</b> - max. <b>200</b> Seiten
                                <br />
                                <b>ab 17,95 €</b>
                                <br />
                                <b>inkl. 24 Seiten</b> & 19% ges.
                                <br />
                                MwSt zzgl. 0,35 € je extra
                                <br />
                                Seite & Versand
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item md={5} xs={6}>
                          <Card elevation={5} className={classes.bookOutputCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="softcover"
                                    checked={values.output === 'softcover'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="Soft-cover"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <br />
                              <Typography variant="caption">
                                mind. <b>24</b> - max. <b>200</b> Seiten
                                <br />
                                <b>ab 17,95 €</b>
                                <br />
                                <b>inkl. 24 Seiten</b> & 19% ges.
                                <br />
                                MwSt zzgl. 0,35 € je extra
                                <br />
                                Seite & Versand
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item md={5} xs={6}>
                          <Card elevation={5} className={classes.bookOutputCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="pdf"
                                    checked={values.output === 'pdf'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="PDF"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <br />
                              <Typography noWrap variant="caption">
                                <b>4,95 €</b>
                                <br />
                                - unbegrenzte Seiten
                                <br />- ohne Wasserzeichen
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography gutterBottom>
                    <b>{'3. Welches Format hättest Du gern?'}</b>
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" disabled={Boolean(renewalLineItemId)}>
                    <RadioGroup
                      aria-label="format"
                      name="format"
                      value={values.format}
                      onChange={handlePreviewIdentityChange}
                    >
                      <Grid container spacing={3}>
                        <Grid item md={5} xs={6}>
                          <Card elevation={2} className={classes.bookFormatCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="211x211"
                                    checked={values.format === '211x211'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="Quadratisch (21x21cm)"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item md={5} xs={6}>
                          <Card elevation={4} className={classes.bookFormatCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="210x297"
                                    checked={values.format === '210x297'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="A4 Hochformat (21x29,7cm)"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item md={5} xs={6}>
                          <Card elevation={4} className={classes.bookFormatCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="297x210"
                                    checked={values.format === '297x210'}
                                    disabled={true}
                                    control={<Radio />}
                                    label="A4 quer (29,7x21cm)"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography gutterBottom>
                    <b>
                      {appVariant === 'bloomup'
                        ? '4. Wie möchtest Du Deine Erlebnisse & Vorhaben dargestellt haben?'
                        : '4. Wie möchtest Du Dein Projekt dargestellt haben?'}
                    </b>
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {'Wähle Dein für Dich passendes Design:'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" disabled={Boolean(renewalLineItemId)}>
                    <RadioGroup aria-label="type" name="type" value={values.type} onChange={handlePreviewIdentityChange}>
                      <Grid container spacing={3}>
                        <Grid item md={5} xs={6}>
                          <Card elevation={5} className={classes.bookTypeCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="day"
                                    checked={values.type === 'day'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="Lebensbuch"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <br />
                              <Typography variant="caption">- all Deine/Eure Einträge mit Texten &amp; Bildern</Typography>
                              <br />
                              <Typography variant="caption">- inkl. Vorwort</Typography>
                              <br />
                              <Typography variant="caption">- auf Wunsch mit Best of my Day &amp; Ideen</Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/*<Grid item xs={5}>
                          <Card elevation={5} className={classes.bookTypeCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="week"
                                    checked={values.type === 'week'}
                                    disabled={true}
                                    control={<Radio />}
                                    label="Wochenübersicht"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <Typography variant="body2">
                                z.B. 8 Wochen im handlichen Überblick inkl. Bilder, Texte, Ideen
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={2}>
                          &nbsp;
                        </Grid>

                        <Grid item xs={5}>
                          <Card elevation={5} className={classes.bookTypeCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="month"
                                    checked={values.type === 'month'}
                                    disabled={true}
                                    control={<Radio />}
                                    label="Monatsübersicht"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <Typography variant="body2">
                                z.B. 1 Jahr im handlichen Überblick inkl. Bilder, Texte, Ideen
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>*/}

                        <Grid item md={5} xs={6}>
                          <Card elevation={5} className={classes.bookTypeCard}>
                            <CardContent>
                              <FormControl component="fieldset">
                                <FormGroup aria-label="position" row>
                                  <FormControlLabel
                                    value="photostream"
                                    checked={values.type === 'photostream'}
                                    disabled={Boolean(renewalLineItemId)}
                                    control={<Radio />}
                                    label="Fotostream"
                                    labelPlacement="start"
                                  />
                                </FormGroup>
                              </FormControl>
                              <br />
                              <Typography variant="caption">Erhalte Deine Fotos in einem Buch - ohne Texte</Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={2}>
                          &nbsp;
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={6} style={{ padding: '8px 0' }}>
                      <Typography>
                        <b>Ich habe einen Gutschein</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl>
                        <TextField
                          variant="outlined"
                          size="small"
                          id="coupon_code"
                          name="coupon_code"
                          autoComplete="coupon_code"
                          value={values.coupon_code}
                          onChange={handleChange}
                          error={touched.coupon_code && Boolean(errors.coupon_code)}
                          helperText={touched.coupon_code && errors.coupon_code}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                &nbsp;
              </Grid>
            </Grid>
            <Grid item md={7} xs={12}>
              <Card elevation={5} style={{ minHeight: '680px' }}>
                <CardContent>
                  <Box p={8}>
                    {renewalLineItemId && (
                      <Button onClick={cancelPrintPreviewRenewal} size="medium">
                        Erneuerung abbrechen
                      </Button>
                    )}
                    {values.printId && ready && (
                      <Button
                        size="medium"
                        variant="contained"
                        color="primary"
                        style={{ float: 'left' }}
                        onClick={(event) => {
                          resetPreview();
                          handleSubmit(event);
                        }}
                      >
                        Neu generieren
                      </Button>
                    )}

                    <Typography align="center" variant="h5" gutterBottom>
                      {'So wird dein Buch aussehen'}
                    </Typography>

                    {numPagesToPrint > 200 && (
                      <Typography variant="body2" color="secondary" style={{ float: 'right', width: '65%' }}>
                        {
                          'ACHTUNG: Die maximale Seitenanzahl von 200 ist erreicht. Um Deinen Print vollständig drucken zu können, müssen '
                        }
                        {Math.ceil(numPagesToPrint / 200)}
                        {' Bücher gedruckt werden.'}
                      </Typography>
                    )}

                    {loading && (
                      <Box p={8} style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                        <Typography variant="body2" align="center">
                          Dein Buch wird generiert.
                        </Typography>
                        <Typography variant="body2" align="center" gutterBottom>
                          Dies kann einige Minuten dauern.
                        </Typography>
                        <CircularProgress style={{ margin: '0 auto' }} />
                      </Box>
                    )}
                    {!loading && !ready && (
                      <Button
                        onClick={handleSubmit}
                        fullWidth
                        size="large"
                        variant="contained"
                        color="primary"
                        disabled={!prints?.length}
                      >
                        Vorschau generieren
                      </Button>
                    )}
                    {values.printId && ready && (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          {user && process.env.REACT_APP_API_URL.includes('my.getbloomup.com') && (
                            <embed
                              src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${process.env.REACT_APP_API_URL.replace(
                                '/v1',
                                ''
                              )}/generated/${values.printId}/${values.format}/${
                                values.type
                              }/print-preview.pdf`} /*/generated/${user?.id}/brandbox-preview.pdf*/
                              width="558"
                              height="558"
                            />
                          )}
                          {user && !process.env.REACT_APP_API_URL.includes('my.getbloomup.com') && (
                            <>
                              <div style={{ color: 'white' }}>(local viewer only)</div>
                              <embed
                                src={`${process.env.REACT_APP_API_URL.split('/v1')[0]}/generated/${values.printId}/${
                                  values.format
                                }/${values.type}/print-preview.pdf`} /*/generated/${user?.id}/brandbox-preview.pdf*/
                                width="558"
                                height="558"
                                type="application/pdf"
                              />
                            </>
                          )}
                          {/*<embed src={brandboxPreview.url} width="558" height="558" ></embed>*/}
                          <p>
                            {brandboxPreview && (
                              <a
                                /*href={`${process.env.REACT_APP_API_URL.replace('/v1', '')}/generated/${currentPrint}/print-preview.pdf`}*/
                                /*download={`${prints.find((print) => print.id === values.print).title}-preview.pdf`}*/
                                /*target="_blank"*/
                                href="#"
                                onClick={() =>
                                  saveAs(
                                    `${process.env.REACT_APP_API_URL.replace('/v1', '')}/generated/${values.printId}/${
                                      values.format
                                    }/${values.type}/print-preview.pdf`,
                                    `${prints.find((print) => print.id === values.printId).title}-preview.pdf`
                                  ) && false
                                }
                              >
                                Gratis PDF Download (mit Wasserzeichen)
                              </a>
                            )}
                          </p>
                          <p>{brandboxError && <p>Error: {brandboxError}</p>}</p>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="h6">
                            <b>
                              {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                                getPriceOfSelection(numPagesToPrint)
                              )}
                            </b>{' '}
                            ({numPagesToPrint} Seiten)
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            onClick={() => (renewalLineItemId ? renewPrintCartLinePreview(values) : addPrintToCart(values))}
                            disabled={!user?.addresses?.billing}
                            fullWidth
                            size="large"
                            variant="contained"
                            color="primary"
                          >
                            {renewalLineItemId ? 'Vorschau übernehmen' : 'zum Warenkorb'}
                          </Button>
                          {cartPersistenceError && (
                            <Typography color="error" variant="caption">
                              Der Warenkorb konnte nicht dauerhaft gespeichert werden.
                            </Typography>
                          )}
                          {!user?.addresses?.billing && (
                            <Typography variant="caption" color="secondary">
                              Du musst in Deinem <a href="/mybloomup#address_settings">Profil</a> erst eine Belegadresse
                              hinterlegen.
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Container>
      {paymentStart && resumePayment && resumePaymentIntent && (
        <StripeCheckoutModal
          modalOpen={paymentStart}
          onComplete={(payment) => generatePaidPrint(payment)}
          onCancel={() => {
            setPaymentStart(false);
            setPaymentIsComplete(null);
            setResumePayment(null);
            setResumePaymentIntent(null);
          }}
          product="print"
          paymentMethods={['card']}
          params={{}}
          cartContents={[]}
          resumePaymentDraftId={resumePayment.id}
          resumePaymentIntent={resumePaymentIntent}
          amount={resumePayment.pricing.totalAmount / 100}
          shippingCosts={resumePayment.pricing.shippingAmount / 100}
        />
      )}
    </div>
  );
};

export default Print;
